import { invoke } from '@tauri-apps/api/core';
import type { Store } from '@nexus/store';
import { isTauri } from '@nexus/store/tauri';

const CLOSE_TO_TRAY = 'desktop_close_to_tray';

/**
 * Desktop preferences: hide to the tray on close (default on) and start at
 * login. `autostart` is null when unavailable: debug builds don't register the
 * plugin, so they never take over the installed app's login entry.
 */
export async function desktopPrefs(
  store: Store,
): Promise<{ closeToTray: boolean; autostart: boolean | null }> {
  const { isEnabled } = await import('@tauri-apps/plugin-autostart');
  return {
    closeToTray: (await store.getSetting(CLOSE_TO_TRAY)) !== 'false',
    autostart: await isEnabled().catch(() => null),
  };
}

export async function setCloseToTray(store: Store, enabled: boolean): Promise<void> {
  await store.setSetting(CLOSE_TO_TRAY, String(enabled));
  await invoke('set_close_to_tray', { enabled });
}

export async function setAutostart(enabled: boolean): Promise<void> {
  const { enable, disable } = await import('@tauri-apps/plugin-autostart');
  await (enabled ? enable() : disable());
}

/** Apply the stored close-to-tray choice at start-up. */
export async function applyDesktopPrefs(store: Store): Promise<void> {
  if (!isTauri()) return;
  await invoke('set_close_to_tray', {
    enabled: (await store.getSetting(CLOSE_TO_TRAY)) !== 'false',
  });
  // Rewrite the login entry with this copy's path. An older build (e.g. a debug
  // build from the project folder) may have left one pointing elsewhere, which
  // then opens at login instead. Only release builds have the plugin.
  try {
    const { isEnabled, enable } = await import('@tauri-apps/plugin-autostart');
    if (await isEnabled()) await enable();
  } catch {
    // Not available here (debug build, Flatpak).
  }
}
